import type { LucideIcon, LucideProps } from 'lucide-react';
import { iconPx, iconStroke, type IconPx, type IconSizeToken } from './iconModel';

export interface IconProps extends Omit<LucideProps, 'ref' | 'size' | 'strokeWidth'> {
  /** The lucide glyph, e.g. `icon={Plus}`. */
  icon: LucideIcon;
  /** A size token (`xs` 12 · `sm` 14 · `md` 16 · `lg` 18 · `xl` 20 · `2xl` 24 · `state` 40 · `state-lg` 48) or its pixel value. Default `md`. */
  size?: IconSizeToken | IconPx;
}

/**
 * The one way to draw a lucide glyph (DESIGN_SYSTEM.md §8, M6 T-54): a size from the
 * icon scale and the single stroke width (1.75; the 40 / 48 empty-state glyphs 1.5). Decorative by default (`aria-hidden`):
 * the button or link around it carries the accessible name. Pass `aria-label` only when
 * the icon is the sole content and nothing else names it.
 *
 * Direction-bearing glyphs (next / back) use `ui/DirIcon` instead.
 *
 * Existing `<Plus size={16} />` calls render the same thing: the scale is enforced by
 * `scripts/ui-metrics.mjs` (iconOffScale) and the stroke by `svg.lucide` in index.css.
 */
export default function Icon({ icon: Glyph, size = 'md', 'aria-hidden': ariaHidden, 'aria-label': ariaLabel, ...rest }: IconProps) {
  const hidden = ariaHidden ?? (ariaLabel ? undefined : true);
  const px = iconPx(size);
  return (
    <Glyph
      size={px}
      strokeWidth={iconStroke(px)}
      aria-hidden={hidden}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
      focusable="false"
      {...rest}
    />
  );
}
